import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  OnboardingChecklistResponse,
  ConfirmTaskResponse,
} from 'types/interfaces/onboarding';
import { meApi } from '../user/meApi';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: baseQuery,
  tagTypes: ['OnboardingChecklist'],
  endpoints: (builder) => ({
    // Get user's onboarding checklist progress
    getOnboardingChecklist: builder.query<OnboardingChecklistResponse, void>({
      query: () => ({
        url: '/onboarding/checklist',
        method: 'GET',
        requiresAuth: true,
      }),
      // Transform response to unwrap the data field
      transformResponse: (response: ApiResponse<OnboardingChecklistResponse>) => response.data,
      providesTags: ['OnboardingChecklist'],
    }),

    // Confirm completion of a manual task (Twitter/Bluesky follow)
    confirmOnboardingTask: builder.mutation<ConfirmTaskResponse, { taskKey: string }>({
      query: ({ taskKey }) => ({
        url: `/onboarding/tasks/${taskKey}/confirm`,
        method: 'POST',
        body: { confirmed: true },
        requiresAuth: true,
      }),
      // Transform response to unwrap the data field
      transformResponse: (response: ApiResponse<ConfirmTaskResponse>) => response.data,
      invalidatesTags: ['OnboardingChecklist'],
      // Also invalidate user data to refresh point balance
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate user cache to refresh point balance in header
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),

    // Claim points for a completed task
    claimOnboardingPoints: builder.mutation<ConfirmTaskResponse, { taskKey: string }>({
      query: ({ taskKey }) => ({
        url: `/onboarding/tasks/${taskKey}/claim`,
        method: 'POST',
        requiresAuth: true,
      }),
      // No transformResponse needed - backend returns data directly (not wrapped)
      invalidatesTags: ['OnboardingChecklist'],
      // Also invalidate user data to refresh point balance in header
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate user cache to refresh point balance in header
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),

    // Bring the Setup Banner back (Asana 1218957945501621): clears
    // onboardingDismissedAt and returns the checklist, same wrapper as the GET.
    undismissOnboarding: builder.mutation<OnboardingChecklistResponse, void>({
      query: () => ({
        url: '/onboarding/undismiss',
        method: 'POST',
        requiresAuth: true,
      }),
      transformResponse: (response: ApiResponse<OnboardingChecklistResponse>) => response.data,
      invalidatesTags: ['OnboardingChecklist'],
    }),


    // Dismiss onboarding (hide it permanently)
    dismissOnboarding: builder.mutation<{ success: boolean; dismissedAt: string }, void>({
      query: () => ({
        url: '/onboarding/dismiss',
        method: 'POST',
        requiresAuth: true,
      }),
      // Backend returns { success, data: { success, dismissedAt } }
      transformResponse: (response: ApiResponse<{ success: boolean; dismissedAt: string }>) => response.data,
      invalidatesTags: ['OnboardingChecklist'],
    }),

    // Funnel telemetry Step 1: email submitted on the Join tab (pre-account, public).
    // Fire-and-forget — never blocks the signup flow.
    onboardingStart: builder.mutation<void, { email: string; referralSource?: string }>({
      query: (body) => ({
        url: '/auth/onboarding-start',
        method: 'POST',
        body,
      }),
    }),

    // Funnel telemetry Step 5: user reached /home (or import) post-onboarding. Backend
    // no-ops after the first call, so calling on every landing is safe.
    reachedHome: builder.mutation<void, void>({
      query: () => ({
        url: '/onboarding/reached-home',
        method: 'POST',
        requiresAuth: true,
      }),
    }),
  }),
});

export const {
  useGetOnboardingChecklistQuery,
  useConfirmOnboardingTaskMutation,
  useClaimOnboardingPointsMutation,
  useDismissOnboardingMutation,
  useUndismissOnboardingMutation,
  useOnboardingStartMutation,
  useReachedHomeMutation,
} = onboardingApi;
